import { create } from "zustand";
import api from "../../utils/api";
import { getError } from "../../utils/getError";

export type RetransmissionScopeType = "all" | "devices" | "group";

export interface RetransmissionScope {
  type: RetransmissionScopeType;
  device_ids: string[];
  group_id: string | null;
}

export interface CompanyRetransmission {
  id: string;
  active: boolean;
  integrator_id: string | null;
  integrator_credentials: Record<string, string>;
  credentials_version: string;
  scope: RetransmissionScope;
  template_overrides: Record<string, unknown>;
  lastRestransmissionAt: { _seconds: number } | null;
  createdAt?: { _seconds: number };
  updatedAt?: { _seconds: number };
}

export interface CompanyRetransmissionFormData {
  active: boolean;
  integrator_id: string;
  integrator_credentials: Record<string, string>;
  credentials_version: string;
  scope: RetransmissionScope;
}

interface RetransmissionFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  active?: string;
}

interface CompanyRetransmissionsStore {
  retransmissions: CompanyRetransmission[];
  loading: boolean;
  fetchRetransmissions: (
    companyId: string,
    params?: RetransmissionFilterParams
  ) => Promise<void>;
  createRetransmission: (
    companyId: string,
    data: CompanyRetransmissionFormData
  ) => Promise<{ success: boolean; error?: string; data?: CompanyRetransmission }>;
  updateRetransmission: (
    companyId: string,
    id: string,
    data: Partial<CompanyRetransmissionFormData>
  ) => Promise<{ success: boolean; error?: string }>;
  deleteRetransmission: (
    companyId: string,
    id: string
  ) => Promise<{ success: boolean; error?: string }>;
  clearRetransmissions: () => void;
}

// Compañía cuya carga es la "vigente". Si una respuesta llega para una
// compañía distinta a la última solicitada, se descarta (evita datos
// obsoletos al cambiar de compañía rápidamente).
let activeRequestCompanyId: string | null = null;

export const useCompanyRetransmissionsStore = create<CompanyRetransmissionsStore>(
  (set, get) => ({
    retransmissions: [],
    loading: false,

    fetchRetransmissions: async (companyId, params) => {
      activeRequestCompanyId = companyId;
      // Limpiar la lista de inmediato para no mostrar datos de otra compañía
      set({ loading: true, retransmissions: [] });
      try {
        const { data } = await api.get("/companyRetransmissionsList", {
          params: {
            company_id: companyId,
            limit: params?.limit ?? 50,
            page: params?.page ?? 1,
            ...(params?.search ? { search: params.search } : {}),
            ...(params?.active ? { active: params.active } : {}),
          },
        });
        if (activeRequestCompanyId !== companyId) return; // respuesta obsoleta
        set({ retransmissions: data.data || [], loading: false });
      } catch (error) {
        console.error("Error fetching retransmissions:", error);
        if (activeRequestCompanyId !== companyId) return;
        set({ retransmissions: [], loading: false });
      }
    },

    createRetransmission: async (companyId, data) => {
      try {
        const response = await api.post("/companyRetransmission", data, {
          params: { company_id: companyId },
        });
        if (response.data?.data) {
          get().fetchRetransmissions(companyId);
          return { success: true, data: response.data.data };
        }
        return { success: false, error: "Error creating retransmission" };
      } catch (error) {
        console.error("Error creating retransmission:", error);
        return { success: false, error: getError(error) };
      }
    },

    updateRetransmission: async (companyId, id, data) => {
      try {
        const response = await api.put("/companyRetransmission", data, {
          params: { company_id: companyId, id },
        });
        if (response.data) {
          get().fetchRetransmissions(companyId);
          return { success: true };
        }
        return { success: false, error: "Error updating retransmission" };
      } catch (error) {
        console.error("Error updating retransmission:", error);
        return { success: false, error: getError(error) };
      }
    },

    deleteRetransmission: async (companyId, id) => {
      try {
        await api.delete("/companyRetransmission", {
          params: { company_id: companyId, id },
        });
        get().fetchRetransmissions(companyId);
        return { success: true };
      } catch (error) {
        console.error("Error deleting retransmission:", error);
        return { success: false, error: getError(error) };
      }
    },

    clearRetransmissions: () => {
      activeRequestCompanyId = null;
      set({ retransmissions: [] });
    },
  })
);
